'use client'

import clsx from 'clsx'
import { ChangeEvent, Fragment, useState } from 'react'
import { useRouter } from '@/lib/next-shim'
import { useTranslations } from '@/lib/next-shim'
import { useDebouncedCallback } from 'use-debounce'
import match from 'autosuggest-highlight/match'
import parse from 'autosuggest-highlight/parse'

import { useSearchMeetingLinksLazyQuery } from '@/core/graphql/hooks'
import { MeetingLink } from '@/core/graphql/types'

import { Combobox } from '@headlessui/react'
import { ChevronUpDownIcon, ExclamationCircleIcon } from '@heroicons/react/24/outline'
import { MagnifyingGlassIcon } from '@heroicons/react/20/solid'

type MeetingLinksAutocompleteProps = {
  type: 'header' | 'float'
}

export function MeetingLinksAutocomplete(props: MeetingLinksAutocompleteProps) {
  const { type = 'header' } = props

  const header = {
    comboboxInput:
      'w-full form-input rounded-md border-0 bg-white py-1.5 pl-3 pr-10 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-primary sm:text-sm sm:leading-6',
    comboOptions: 'absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white text-base shadow-lg ring-1 ring-black ring-opacity-5 sm:text-sm',
    glassIcon: false,
  }

  const float = {
    comboboxInput: 'h-12 w-full border-0 bg-transparent pl-11 pr-4 text-gray-900 placeholder:text-gray-400 focus:ring-0 sm:text-sm outline-none',
    comboOptions: 'max-h-96 scroll-py-3 overflow-y-auto p-3',
    glassIcon: true,
  }

  const getType = (t: string) => {
    switch (t) {
      case 'header':
        return header
      case 'float':
        return float
      default:
        return header
    }
  }

  const router = useRouter()
  const t = useTranslations('marketing.sections.Hero')
  const [inputValue, setInputValue] = useState('')

  const [searchMeetingLinks, { data }] = useSearchMeetingLinksLazyQuery()

  const meetingLinksSearchResults = data?.meetingLinksSearch

  // const directory: Record<string, Array<{ label: string; link: string; value: string }>> = useMemo(
  //   () => ({
  //     establishment: establishments.map((establishment) => ({ label: establishment.name, link: `/${lang}/${establishment.slug}`, value: establishment.slug })),
  //     meetingLink: meetingLinks.map((meetingLink) => ({
  //       label: meetingLink.name,
  //       link: `/${lang}/meeting-links/${meetingLink.slug}`,
  //       value: meetingLink.slug,
  //     })),
  //     staff: staffs.map((staff) => ({
  //       label: `${staff.name} ${staff.surname}`,
  //       link: `/${lang}/${staff.etablissement?.slug}/${staff.slug}`,
  //       value: staff.slug,
  //     })),
  //   }),
  //   [establishments, meetingLinks, staffs]
  // )

  const debouncedSearchMeetingLinks = useDebouncedCallback((phrase: string) => {
    searchMeetingLinks({ variables: { phrase } })
  }, 300)

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    setInputValue(event.target.value)
    debouncedSearchMeetingLinks(event.target.value)
  }

  const handleLoad = () => searchMeetingLinks({ variables: { phrase: 't' } })

  const handleSelect = (link: string | null) => {
    if (link) router.push(link)
  }

  return (
    <Combobox as="div" nullable onChange={handleSelect}>
      <div className="relative" role="search" aria-label="Search consultations">
        {getType(type).glassIcon && <MagnifyingGlassIcon className="pointer-events-none absolute left-4 top-3.5 h-5 w-5 text-gray-400" aria-hidden="true" />}

        <Combobox.Input
          onClick={handleLoad}
          className={getType(type).comboboxInput}
          displayValue={(meetingLink: MeetingLink | null) => meetingLink?.name || inputValue}
          onChange={handleChange}
          type="search"
        />
        <Combobox.Button className="absolute bg-transparent inset-y-0 right-0 flex items-center rounded-r-md px-2 focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary">
          <ChevronUpDownIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
        </Combobox.Button>

        {!!meetingLinksSearchResults && (
          <Combobox.Options className={getType(type).comboOptions}>
            {meetingLinksSearchResults.length === 0 ? (
              <div className="px-6 py-8 text-center text-sm">
                <ExclamationCircleIcon className="mx-auto h-6 w-6 text-gray-400" aria-hidden="true" />
                <p className="mt-3 font-semibold text-gray-900">{t('meetingLinksAutocomplete.noResults.title')}</p>
                <p className="mt-1 text-gray-500">{t('meetingLinksAutocomplete.noResults.description')}</p>
              </div>
            ) : (
              meetingLinksSearchResults.map((searchResult) => {
                if (searchResult.__typename !== 'MeetingLink') return null
                const meetingLink = searchResult
                return (
                  <Combobox.Option
                    key={meetingLink.slug}
                    value={`/meeting-links/${meetingLink.slug}`}
                    className={({ active }) => clsx('relative cursor-pointer select-none py-2 pl-6 pr-9', active ? 'bg-primary text-white' : 'text-gray-900')}
                  >
                    {({ active, selected }) => {
                      const meetingLinkNameMatches = match(meetingLink.name, inputValue, { insideWords: true })
                      const meetingLinkNameParts = parse(meetingLink.name, meetingLinkNameMatches)

                      return (
                        <div>
                          <div className="flex items-center justify-between">
                            <span className={clsx('block truncate', selected && 'font-semibold')}>
                              {meetingLinkNameParts.map((part, idx) => (
                                <span key={`${meetingLink.slug}-${idx}`} className={clsx({ 'font-semibold': part.highlight })}>
                                  {part.text}
                                </span>
                              ))}
                            </span>
                            <span
                              className={clsx('inline-flex items-center rounded-md px-2 py-1 text-xs font-medium', {
                                'bg-blue-100 text-blue-700': !active,
                                'bg-blue-400/10 text-blue-400 ring-1 ring-inset ring-blue-400/30': active,
                              })}
                            >
                              {t('meetingLinksAutocomplete.tag.meetingLink')}
                            </span>
                          </div>
                          <div className="flex items-center gap-x-2.5 text-xs leading-5 text-gray-400">
                            {meetingLink.specialities.map((speciality, specialityIdx) => {
                              const specialityNameMatches = match(speciality.name, inputValue, { insideWords: true })
                              const specialityNameParts = parse(speciality.name, specialityNameMatches)

                              return (
                                <Fragment key={speciality.code}>
                                  <p className="truncate whitespace-nowrap">
                                    {specialityNameParts.map((part, idx) => (
                                      <span key={`${speciality.code}-${idx}`} className={clsx({ 'font-semibold': part.highlight })}>
                                        {part.text}
                                      </span>
                                    ))}
                                  </p>
                                  {specialityIdx !== meetingLink.specialities.length - 1 && (
                                    <svg viewBox="0 0 2 2" className="h-0.5 w-0.5 flex-none fill-gray-300">
                                      <circle cx={1} cy={1} r={1} />
                                    </svg>
                                  )}
                                </Fragment>
                              )
                            })}
                          </div>
                        </div>
                      )
                    }}
                  </Combobox.Option>
                )
              })
            )}
          </Combobox.Options>
        )}
      </div>
    </Combobox>
  )
}
